import React, { useCallback } from "react";
import { useApi } from "../hooks/useApi.ts";

interface TaskGroup {
  id: number;
  name: string;
  status: string;
  source_type: string;
  created_at: string;
  subtask_count: number;
}

interface Job {
  name: string;
  intervalMs: number;
  lastRun: string | null;
  isRunning: boolean;
  enabled: boolean;
}

interface ConfigData {
  active: {
    teamwork: { hasApiKey: boolean };
    fellow: { hasApiKey: boolean };
  };
}

const JOB_KEY_MAP: Record<string, { key: keyof ConfigData["active"]; label: string }> = {
  "poll-fellow": { key: "fellow", label: "Fellow" },
  "check-due-dates": { key: "teamwork", label: "Teamwork" },
};

const JOB_DESCRIPTIONS: Record<string, string> = {
  "poll-fellow": "Periodically checks Fellow for new meeting recordings and transcripts, storing them locally for processing into tasks.",
  "check-due-dates": "Scans for upcoming task due dates and sends email reminders at your configured intervals (e.g. 7, 3, 1 days before).",
};

export function Dashboard() {
  const { data: taskGroups } = useApi<TaskGroup[]>("/api/task-groups");
  const { data: jobs, refetch: refetchJobs } = useApi<Job[]>("/api/jobs");
  const { data: config } = useApi<ConfigData>("/api/config");

  const toggleJob = useCallback(async (name: string, enabled: boolean) => {
    await fetch(`/api/jobs/${name}/enabled`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    });
    refetchJobs();
  }, [refetchJobs]);

  const pendingReview = taskGroups?.filter((g) => g.status === "pending_review").length ?? 0;
  const created = taskGroups?.filter((g) => g.status === "created").length ?? 0;
  const drafts = taskGroups?.filter((g) => g.status === "draft").length ?? 0;

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Overview of your automated project management</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{pendingReview}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{created}</div>
          <div className="stat-label">Tasks Created</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{drafts}</div>
          <div className="stat-label">Drafts</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{taskGroups?.length ?? 0}</div>
          <div className="stat-label">Total Task Groups</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Recent Task Groups</h2>
        </div>
        {taskGroups && taskGroups.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Source</th>
                <th>Status</th>
                <th>Subtasks</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {taskGroups.slice(0, 10).map((group) => (
                <tr key={group.id}>
                  <td>{group.name}</td>
                  <td>
                    <span className="badge badge-info">{group.source_type}</span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        group.status === "created"
                          ? "badge-success"
                          : group.status === "pending_review"
                            ? "badge-warning"
                            : "badge-neutral"
                      }`}
                    >
                      {group.status}
                    </span>
                  </td>
                  <td>{group.subtask_count}</td>
                  <td>{new Date(group.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            No task groups yet. Upload a SOW or process a transcript to get started.
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Background Jobs</h2>
        </div>
        <p style={{ padding: "0 1.5rem", color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>
          These jobs run automatically on a schedule when enabled. They require the corresponding API keys to be configured in Settings.
        </p>
        {jobs && jobs.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Job</th>
                <th>Description</th>
                <th>Interval</th>
                <th>Last Run</th>
                <th>Status</th>
                <th>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const mapping = JOB_KEY_MAP[job.name];
                const missingKey = mapping && config ? !config.active[mapping.key].hasApiKey : false;
                return (
                  <tr key={job.name}>
                    <td>{job.name}</td>
                    <td style={{ fontSize: "0.85em", color: "var(--color-text-muted)", maxWidth: "300px" }}>
                      {JOB_DESCRIPTIONS[job.name] ?? "—"}
                    </td>
                    <td>{Math.round(job.intervalMs / 60000)}m</td>
                    <td>
                      {job.lastRun
                        ? new Date(job.lastRun).toLocaleString()
                        : "Never"}
                    </td>
                    <td>
                      {missingKey ? (
                        <span className="badge badge-danger" title={`${mapping!.label} API key not set`}>
                          No API Key — <a href="#settings" style={{ color: "inherit", textDecoration: "underline" }}>Settings</a>
                        </span>
                      ) : !job.enabled ? (
                        <span className="badge badge-neutral">Disabled</span>
                      ) : (
                        <span
                          className={`badge ${job.isRunning ? "badge-warning" : "badge-success"}`}
                        >
                          {job.isRunning ? "Running" : "Idle"}
                        </span>
                      )}
                    </td>
                    <td>
                      <label className="toggle-switch" title={missingKey ? `Configure ${mapping?.label} API key first` : ""}>
                        <input
                          type="checkbox"
                          checked={job.enabled}
                          disabled={missingKey}
                          onChange={() => toggleJob(job.name, !job.enabled)}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">No jobs configured.</div>
        )}
      </div>
    </div>
  );
}
